import React, { useState } from 'react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { AlertCircle } from 'lucide-react';
import { spacing } from '@/lib/design-tokens/spacing';
import { typography } from '@/lib/design-tokens/typography';

interface LinkedInUrlDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (url: string) => void;
}

export const LinkedInUrlDialog: React.FC<LinkedInUrlDialogProps> = ({
  open,
  onOpenChange,
  onSubmit,
}) => {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');

  const validateLinkedInUrl = (input: string): boolean => {
    if (!input || !input.trim()) {
      setError('Please enter a URL');
      return false;
    }

    const trimmedUrl = input.trim();
    
    // Check if it's a valid LinkedIn company URL pattern
    const linkedInPattern = /^https?:\/\/(www\.)?linkedin\.com\/company\/[a-zA-Z0-9-]+\/?$/;
    
    if (!linkedInPattern.test(trimmedUrl)) {
      setError('Please enter a valid LinkedIn company page URL (e.g., https://linkedin.com/company/company-name)');
      return false;
    }

    // Check length
    if (trimmedUrl.length > 500) {
      setError('URL is too long');
      return false;
    }

    setError('');
    return true;
  };

  const handleSubmit = () => {
    if (validateLinkedInUrl(url)) {
      onSubmit(url.trim());
      setUrl('');
      setError('');
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    setUrl('');
    setError('');
    onOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add LinkedIn Company Page</DialogTitle>
          <DialogDescription>
            Enter the official LinkedIn company page URL below
          </DialogDescription>
        </DialogHeader>
        
        <DialogBody className={spacing.modalFormSpacing}>
          <div className={spacing.modalFieldSpacing}>
            <Label htmlFor="linkedin-url" className={typography.label}>
              LinkedIn Company Page URL
            </Label>
            <Input
              id="linkedin-url"
              placeholder="https://linkedin.com/company/company-name"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                if (error) setError(''); // Clear error on typing
              }}
              onKeyDown={handleKeyDown}
              hasError={!!error}
              autoFocus
            />
            {error && (
              <div className="flex items-center gap-2 text-sm text-destructive mt-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <p className={`${typography.subtle} mt-1.5`}>
              Example: https://linkedin.com/company/google
            </p>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>
            Verify LinkedIn Page
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
